import {
    Box,
    Chip,
    Divider,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { AlertTriangle, CheckCircle2, Layers, Link, Plus } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { TargetImageInfo } from './types';
import { NEW_SS_SENTINEL } from './constants';
import ImageThumbnailCard from './ImageThumbnailCard';
import RegistrationRadioRow from './RegistrationRadioRow';

type TargetImageSelectionPanelProps = {
    targetImages: TargetImageInfo[];
    selectedImageSeriesUid: string | null;
    selectedImageSsUid: string | null;
    availableRegistrations: RigidRegistration[];
    selectedRegistrationUid: string | null;
    sourceImage: Image3D;
    onSelectImage: (seriesUid: string) => void;
    onSelectStructureSet: (ssUid: string) => void;
    onSelectRegistration: (regUid: string) => void;
};

export default function TargetImageSelectionPanel({
    targetImages,
    selectedImageSeriesUid,
    selectedImageSsUid,
    availableRegistrations,
    selectedRegistrationUid,
    sourceImage,
    onSelectImage,
    onSelectStructureSet,
    onSelectRegistration,
}: TargetImageSelectionPanelProps) {
    const intl = useIntl();
    const theme = useTheme();

    return (
        <Box
            sx={{
                flex: 1,
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box px={2} py={1}>
                <Typography
                    fontSize={12}
                    color="text.secondary"
                    fontWeight={500}
                >
                    <FormattedMessage
                        id="structureSet.copy.selectTarget"
                        defaultMessage="Copy to"
                    />
                </Typography>
            </Box>
            <Divider />

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {targetImages.length === 0 ? (
                    <Box px={2} py={4} textAlign="center">
                        <Typography fontSize={12} color="text.secondary">
                            <FormattedMessage
                                id="structureSet.copy.noTargetImages"
                                defaultMessage="No images available."
                            />
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {targetImages.map(
                            ({
                                image,
                                isSourceImage,
                                geometryMatch,
                                sameFrameOfReference,
                                registrations: imgRegs,
                                structureSets: imgSets,
                            }) => {
                                const isSelected =
                                    selectedImageSeriesUid === image.seriesUid;
                                const isAvailable =
                                    isSourceImage ||
                                    geometryMatch ||
                                    sameFrameOfReference ||
                                    imgRegs.length > 0;

                                const statusIcon = geometryMatch ? (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'structureSet.copy.geometryMatch',
                                            defaultMessage:
                                                'Identical geometry — direct copy',
                                        })}
                                    >
                                        <Box display="flex">
                                            <CheckCircle2
                                                size={12}
                                                color="#2e7d32"
                                            />
                                        </Box>
                                    </Tooltip>
                                ) : sameFrameOfReference ? (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'structureSet.copy.sameFrameOfReference',
                                            defaultMessage:
                                                'Same coordinate space — structures will be resampled',
                                        })}
                                    >
                                        <Box display="flex">
                                            <Layers
                                                size={12}
                                                color={theme.palette.info.main}
                                            />
                                        </Box>
                                    </Tooltip>
                                ) : imgRegs.length > 0 ? (
                                    <Tooltip
                                        title={intl.formatMessage(
                                            {
                                                id: 'structureSet.copy.registrationAvailable',
                                                defaultMessage:
                                                    '{count} {count, plural, one {registration} other {registrations}} available',
                                            },
                                            { count: imgRegs.length }
                                        )}
                                    >
                                        <Box display="flex">
                                            <Link
                                                size={12}
                                                color={theme.palette.info.main}
                                            />
                                        </Box>
                                    </Tooltip>
                                ) : !isSourceImage ? (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'structureSet.copy.geometryMismatch',
                                            defaultMessage:
                                                'Different geometry — no registration available',
                                        })}
                                    >
                                        <Box
                                            display="flex"
                                            sx={{ pointerEvents: 'auto' }}
                                        >
                                            <AlertTriangle
                                                size={12}
                                                color={
                                                    theme.palette.warning.main
                                                }
                                            />
                                        </Box>
                                    </Tooltip>
                                ) : null;

                                return (
                                    <Box key={image.seriesUid}>
                                        <ImageThumbnailCard
                                            image={image}
                                            isSelected={isSelected}
                                            isAvailable={isAvailable}
                                            sharedFor={
                                                !isSourceImage &&
                                                image.frameOfReferenceUid ===
                                                    sourceImage.frameOfReferenceUid
                                            }
                                            statusIcon={statusIcon}
                                            onClick={() =>
                                                onSelectImage(image.seriesUid)
                                            }
                                        />

                                        {/* Structure sets — always visible */}
                                        {(imgSets.length > 0 ||
                                            isAvailable) && (
                                            <Box sx={{ pl: 7, pr: 2, pb: 0.5 }}>
                                                <RadioGroup
                                                    value={
                                                        isSelected
                                                            ? selectedImageSsUid
                                                            : ''
                                                    }
                                                    onChange={(_, v) => {
                                                        onSelectImage(
                                                            image.seriesUid
                                                        );
                                                        onSelectStructureSet(v);
                                                    }}
                                                >
                                                    {imgSets.map((ss) => {
                                                        const ssUid =
                                                            ss.structureSet
                                                                .dicomStructureSet
                                                                .sopInstanceUid;
                                                        const isApproved =
                                                            ss.structureSet
                                                                .approvalStatus ===
                                                            DICOMApprovalStatus.APPROVED;
                                                        const isSsSelected =
                                                            isSelected &&
                                                            selectedImageSsUid ===
                                                                ssUid;

                                                        return (
                                                            <Tooltip
                                                                key={ssUid}
                                                                title={
                                                                    isApproved
                                                                        ? intl.formatMessage(
                                                                              {
                                                                                  id: 'structureSet.copy.approvedTarget',
                                                                                  defaultMessage:
                                                                                      'Cannot copy into an approved structure set.',
                                                                              }
                                                                          )
                                                                        : ''
                                                                }
                                                                placement="bottom"
                                                            >
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={0.5}
                                                                    py={0.25}
                                                                    sx={{
                                                                        cursor:
                                                                            isApproved ||
                                                                            !isAvailable
                                                                                ? 'default'
                                                                                : 'pointer',
                                                                        borderRadius: 0.5,
                                                                        px: 0.5,
                                                                        opacity:
                                                                            isApproved ||
                                                                            !isAvailable
                                                                                ? 0.5
                                                                                : 1,
                                                                    }}
                                                                    onClick={() => {
                                                                        if (
                                                                            isApproved ||
                                                                            !isAvailable
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        onSelectImage(
                                                                            image.seriesUid
                                                                        );
                                                                        onSelectStructureSet(
                                                                            ssUid
                                                                        );
                                                                    }}
                                                                >
                                                                    <Radio
                                                                        size="small"
                                                                        value={
                                                                            ssUid
                                                                        }
                                                                        checked={
                                                                            isSsSelected
                                                                        }
                                                                        disabled={
                                                                            isApproved ||
                                                                            !isAvailable
                                                                        }
                                                                        sx={{
                                                                            p: 0.25,
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        fontSize={
                                                                            11
                                                                        }
                                                                        noWrap
                                                                        color={
                                                                            isSsSelected
                                                                                ? 'text.primary'
                                                                                : 'text.secondary'
                                                                        }
                                                                    >
                                                                        <SensitiveInfoTypography>
                                                                            {
                                                                                ss.label
                                                                            }
                                                                        </SensitiveInfoTypography>
                                                                    </Typography>
                                                                    <Typography
                                                                        fontSize={
                                                                            10
                                                                        }
                                                                        color="text.secondary"
                                                                    >
                                                                        (
                                                                        {
                                                                            ss
                                                                                .structureSet
                                                                                .roiArray
                                                                                .length
                                                                        }
                                                                        )
                                                                    </Typography>
                                                                    {isApproved && (
                                                                        <Chip
                                                                            label={intl.formatMessage(
                                                                                {
                                                                                    id: 'structureSet.copy.approved',
                                                                                    defaultMessage:
                                                                                        'Approved',
                                                                                }
                                                                            )}
                                                                            size="small"
                                                                            sx={{
                                                                                fontSize: 9,
                                                                                height: 16,
                                                                                ml: 0.5,
                                                                            }}
                                                                            color="default"
                                                                            variant="outlined"
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    })}

                                                    {isAvailable && (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.5}
                                                            py={0.25}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                borderRadius: 0.5,
                                                                px: 0.5,
                                                            }}
                                                            onClick={() => {
                                                                onSelectImage(
                                                                    image.seriesUid
                                                                );
                                                                onSelectStructureSet(
                                                                    NEW_SS_SENTINEL
                                                                );
                                                            }}
                                                        >
                                                            <Radio
                                                                size="small"
                                                                value={
                                                                    NEW_SS_SENTINEL
                                                                }
                                                                checked={
                                                                    isSelected &&
                                                                    selectedImageSsUid ===
                                                                        NEW_SS_SENTINEL
                                                                }
                                                                sx={{ p: 0.25 }}
                                                            />
                                                            <Plus
                                                                size={11}
                                                                color={
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main
                                                                }
                                                            />
                                                            <Typography
                                                                fontSize={11}
                                                                color="primary"
                                                            >
                                                                <FormattedMessage
                                                                    id="structureSet.copy.createNew"
                                                                    defaultMessage="Create new structure set"
                                                                />
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </RadioGroup>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            }
                        )}

                        {/* Registrations section */}
                        {availableRegistrations.length > 0 && (
                            <>
                                <Divider />
                                <Box px={2} py={0.75}>
                                    <Typography
                                        fontSize={10}
                                        color="text.secondary"
                                        fontWeight={500}
                                        textTransform="uppercase"
                                        letterSpacing={0.5}
                                    >
                                        <FormattedMessage
                                            id="structureSet.copy.registrations"
                                            defaultMessage="Registrations ({count})"
                                            values={{
                                                count: availableRegistrations.length,
                                            }}
                                        />
                                    </Typography>
                                </Box>
                                {availableRegistrations.map((reg) => (
                                    <RegistrationRadioRow
                                        key={reg.sopInstanceUid}
                                        reg={reg}
                                        isSelected={
                                            selectedRegistrationUid ===
                                            reg.sopInstanceUid
                                        }
                                        onClick={() =>
                                            onSelectRegistration(
                                                reg.sopInstanceUid
                                            )
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
